import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { Campo, Entrada } from '@/components/ui/Campo'
import {
  Dialogo,
  DialogoCerrar,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoPie,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import { SelectorFecha } from '@/components/ui/SelectorFecha'
import { EditorTexto } from '@/components/ui/TextoConFormato'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { mensajeDeError } from '@/features/admin/ModalMarco'
import {
  TOPE_DESCRIPCION_PLAN,
  TOPE_TITULO_PLAN,
  esDeGoogleDocs,
  planificacionEntrada,
  type PlanificacionEntrada,
  type PlanificacionResumen,
} from '@/lib/schemas/planificacion'
import { BotonDriveEnlace } from '@/features/publicacion/ArchivosDeDrive'
import { useDocumentoPublico, useGuardarPlanificacion } from './api'

interface ModalPlanificacionProps {
  abierto: boolean
  alCerrar: () => void
  proyectoId: string
  /** Null: nueva. */
  planificacion: PlanificacionResumen | null
  /** Período precargado para una nueva: el mes siguiente al de la última. */
  proximoDesde: string
  proximoHasta: string
  /** Carpeta de Drive del cliente: ahí se abre el selector (Fase 6). */
  carpetaDrive?: string | null
}

/**
 * C11 · Nueva / Editar planificación (Fase 5a). Un solo paso. Nueva: Guardar
 * borrador o Enviar a revisión. Edición: Guardar cambios, sin tocar el estado.
 */
export function ModalPlanificacion(props: ModalPlanificacionProps) {
  // Se monta de cero en cada apertura: los valores arrancan de lo guardado.
  if (!props.abierto) return null
  return <Formulario {...props} />
}

function Formulario({
  alCerrar,
  proyectoId,
  planificacion,
  proximoDesde,
  proximoHasta,
  carpetaDrive = null,
}: ModalPlanificacionProps) {
  const guardar = useGuardarPlanificacion()
  const avisar = useAvisar()
  const [v, setV] = useState<PlanificacionEntrada>(() => ({
    proyectoId,
    titulo: planificacion?.titulo ?? '',
    fechaDesde: planificacion?.fechaDesde ?? proximoDesde,
    fechaHasta: planificacion?.fechaHasta ?? proximoHasta,
    descripcion: planificacion?.descripcion ?? '',
    urlDocumento: planificacion?.urlDocumento ?? '',
    enviar: false,
  }))
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const publico = useDocumentoPublico(v.urlDocumento.trim())

  const cambiar = (parcial: Partial<PlanificacionEntrada>) => {
    setV((a) => ({ ...a, ...parcial }))
    setErrores({})
    setErrorGeneral(null)
  }

  const enviar = (mandarARevision: boolean) => {
    const datos = { ...v, enviar: mandarARevision }
    const r = planificacionEntrada.safeParse(datos)
    if (!r.success) {
      const porCampo: Record<string, string> = {}
      for (const issue of r.error.issues)
        porCampo[String(issue.path[0] ?? '')] ??= issue.message
      setErrores(porCampo)
      return
    }
    setOcupado(true)
    guardar
      .mutateAsync({ id: planificacion?.id ?? null, datos })
      .then(() => {
        avisar(
          planificacion
            ? 'Planificación guardada'
            : mandarARevision
              ? 'Planificación enviada a revisión'
              : 'Borrador guardado',
        )
        alCerrar()
      })
      .catch((e: unknown) => {
        setErrorGeneral(mensajeDeError(e))
      })
      .finally(() => {
        setOcupado(false)
      })
  }

  const url = v.urlDocumento.trim()
  const avisoDocumento =
    url && !esDeGoogleDocs(url)
      ? 'No parece un documento de Google Docs. Podés guardarlo igual.'
      : publico.data?.publico === false
        ? 'El cliente no va a poder abrirlo: compartilo como "cualquier persona con el enlace" (con permiso para comentar).'
        : null

  return (
    <Dialogo
      open
      onOpenChange={(o) => {
        if (!o) alCerrar()
      }}
    >
      <DialogoContenido aria-busy={ocupado}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            enviar(false)
          }}
        >
          <DialogoEncabezado>
            <DialogoTitulo className="text-xl font-semibold tracking-tight">
              {planificacion ? 'Editar planificación' : 'Nueva planificación'}
            </DialogoTitulo>
            <DialogoDescripcion className="text-sm text-tinta-suave">
              El plan del período, con el enlace al documento de Google Docs.
            </DialogoDescripcion>
          </DialogoEncabezado>

          <DialogoCuerpo className="flex flex-col gap-4">
            <Campo
              etiqueta="Título"
              ayuda="Sugerido: mes y foco. Ej.: Octubre 2026 · Carta de otoño"
              error={errores.titulo}
              extremo={`${String(v.titulo.length)} / ${String(TOPE_TITULO_PLAN)}`}
            >
              {(p) => (
                <Entrada
                  id={p.id}
                  aria-invalid={p.invalido}
                  aria-describedby={p.describedBy}
                  value={v.titulo}
                  maxLength={TOPE_TITULO_PLAN}
                  autoFocus
                  onChange={(e) => {
                    cambiar({ titulo: e.target.value })
                  }}
                />
              )}
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Desde" error={errores.fechaDesde}>
                {(p) => (
                  <SelectorFecha
                    id={p.id}
                    invalido={p.invalido}
                    valor={v.fechaDesde}
                    alCambiar={(fechaDesde) => {
                      cambiar({ fechaDesde })
                    }}
                  />
                )}
              </Campo>
              <Campo etiqueta="Hasta" error={errores.fechaHasta}>
                {(p) => (
                  <SelectorFecha
                    id={p.id}
                    invalido={p.invalido}
                    valor={v.fechaHasta}
                    alCambiar={(fechaHasta) => {
                      cambiar({ fechaHasta })
                    }}
                  />
                )}
              </Campo>
            </div>

            <BotonDriveEnlace
              carpetaId={carpetaDrive}
              modo="archivos"
              multiple={false}
              tipos="application/vnd.google-apps.document,application/pdf"
              titulo="Elegí el documento de la planificación"
              etiqueta="Elegir documento de Drive"
              alElegir={(lista) => {
                const d = lista[0]
                if (d) cambiar({ urlDocumento: d.url })
              }}
            />

            <Campo
              etiqueta="Documento principal"
              ayuda={
                avisoDocumento ?? 'El enlace del documento de Google Docs con el plan.'
              }
              error={errores.urlDocumento}
            >
              {(p) => (
                <Entrada
                  id={p.id}
                  type="url"
                  inputMode="url"
                  placeholder="https://docs.google.com/document/d/…"
                  aria-invalid={p.invalido}
                  aria-describedby={p.describedBy}
                  data-aviso-documento={avisoDocumento ? 'si' : 'no'}
                  className={avisoDocumento ? 'border-cambios-tinta' : undefined}
                  value={v.urlDocumento}
                  onChange={(e) => {
                    cambiar({ urlDocumento: e.target.value })
                  }}
                />
              )}
            </Campo>

            <Campo
              etiqueta="Descripción"
              ayuda="Opcional. Podés pegar enlaces a otros documentos."
              error={errores.descripcion}
              extremo={`${String((v.descripcion ?? '').length)} / ${String(TOPE_DESCRIPCION_PLAN)}`}
            >
              {(p) => (
                <EditorTexto
                  id={p.id}
                  valor={v.descripcion ?? ''}
                  tope={TOPE_DESCRIPCION_PLAN}
                  invalido={p.invalido}
                  describedBy={p.describedBy}
                  alCambiar={(descripcion) => {
                    cambiar({ descripcion })
                  }}
                />
              )}
            </Campo>

            {errorGeneral && (
              <p
                role="alert"
                className="rounded-xl bg-nuevo/10 px-3.5 py-2.5 text-sm text-nuevo"
              >
                {errorGeneral}
              </p>
            )}
          </DialogoCuerpo>

          <DialogoPie>
            <DialogoCerrar asChild>
              <Boton variante="secundario" disabled={ocupado}>
                Cancelar
              </Boton>
            </DialogoCerrar>
            {planificacion ? (
              <Boton type="submit" disabled={ocupado}>
                Guardar cambios
              </Boton>
            ) : (
              <>
                <Boton type="submit" variante="secundario" disabled={ocupado}>
                  Guardar borrador
                </Boton>
                <Boton
                  type="button"
                  disabled={ocupado}
                  onClick={() => {
                    enviar(true)
                  }}
                >
                  Enviar a revisión
                </Boton>
              </>
            )}
          </DialogoPie>
        </form>
      </DialogoContenido>
    </Dialogo>
  )
}
